import { offlineDb } from "./db";
import { mirrorSubmission, removeMirroredSubmission } from "./native-mirror";
import type { QueuedSubmission, QueueStatus } from "./types";

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function generateIdempotencyKey(
  formUrl: string,
  localId: string,
): string {
  return `${formUrl}:${localId}`;
}

export async function enqueueSubmission(params: {
  formUrl: string;
  formVersion: string;
  jobId?: string | null;
  sourceDraftId?: string | null;
  payload: Record<string, unknown>;
}): Promise<QueuedSubmission> {
  const localSubmissionId = generateId();
  const now = new Date().toISOString();

  const submission: QueuedSubmission = {
    localSubmissionId,
    formId: 0, // resolved server-side via formUrl
    formUrl: params.formUrl,
    formVersion: params.formVersion,
    jobId: params.jobId ?? null,
    sourceDraftId: params.sourceDraftId ?? null,
    idempotencyKey: generateIdempotencyKey(params.formUrl, localSubmissionId),
    payloadJson: params.payload,
    status: "queued",
    retryCount: 0,
    lastError: null,
    createdAt: now,
    updatedAt: now,
    syncedAt: null,
  };

  await offlineDb.queuedSubmissions.put(submission);
  // Native only: also write a durable copy to the OS sandbox so the record
  // survives IndexedDB eviction. No-op + instant on web; never throws.
  await mirrorSubmission(submission);
  return submission;
}

export async function updateSubmissionStatus(
  localSubmissionId: string,
  status: QueueStatus,
  extra?: Partial<
    Pick<
      QueuedSubmission,
      "lastError" | "retryCount" | "syncedAt" | "serverConfirmedAt"
    >
  >,
): Promise<void> {
  await offlineDb.queuedSubmissions.update(localSubmissionId, {
    status,
    updatedAt: new Date().toISOString(),
    ...extra,
  });
}

export async function getQueuedSubmissions(): Promise<QueuedSubmission[]> {
  // Only auto-drive `queued`. `failed` rows are a dead-letter state: they have
  // already exhausted MAX_RETRIES (or hit a non-retryable error), so re-selecting
  // them here just churns them straight back to `failed` every cycle. They wait
  // for an explicit `retryFailed` (which resets retryCount) surfaced in the UI.
  return offlineDb.queuedSubmissions
    .where("status")
    .equals("queued")
    .sortBy("createdAt");
}

export async function getSyncingSubmissions(): Promise<QueuedSubmission[]> {
  return offlineDb.queuedSubmissions
    .where("status")
    .equals("syncing")
    .toArray();
}

export async function getAllSubmissionsForForm(
  formUrl: string,
): Promise<QueuedSubmission[]> {
  return offlineDb.queuedSubmissions
    .where("formUrl")
    .equals(formUrl)
    .reverse()
    .sortBy("createdAt");
}

/**
 * Delete local copies of submissions only once they are both (a) marked
 * `synced` AND (b) positively confirmed present on the server by the reconcile
 * sweep (`serverConfirmedAt` set), plus a grace period. Gating on reconcile —
 * not just the 200 OK — means we never discard the only local copy of a record
 * on the strength of a response that might have been lost in flight or a write
 * the server later rolled back.
 */
export async function clearSyncedSubmissions(graceMs = 24 * 60 * 60 * 1000): Promise<number> {
  const cutoff = new Date(Date.now() - graceMs).toISOString();
  const stale = await offlineDb.queuedSubmissions
    .where("status")
    .equals("synced")
    .filter(
      (s) =>
        s.serverConfirmedAt != null &&
        s.serverConfirmedAt < cutoff,
    )
    .toArray();

  const ids = stale.map((s) => s.localSubmissionId);
  await offlineDb.queuedSubmissions.bulkDelete(ids);
  // Keep the native mirror in lockstep: drop each confirmed-synced record's
  // durable copy (and its mirrored photo blobs). No-op on web; never throws.
  for (const submission of stale) {
    await removeMirroredSubmission(submission);
  }
  return ids.length;
}
